import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ShoppingBag, BellRing, Minus, Plus } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { StarRating } from "../components/StarRating";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("category") || "All";
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(initialCat);
  const [query, setQuery] = useState("");
  const { addItem, items, updateQty } = useCart();
  const [notifyFor, setNotifyFor] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifying, setNotifying] = useState(false);

  const submitNotify = async (e) => {
    e.preventDefault();
    if (!notifyEmail) return;
    setNotifying(true);
    try {
      const { data } = await api.post("/stock-notifications", { product_id: notifyFor.product_id, email: notifyEmail });
      toast.success(data.message);
      setNotifyFor(null);
      setNotifyEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setNotifying(false);
    }
  };

  useEffect(() => {
    api.get("/products/categories").then(({ data }) => setCategories(["All", ...data]));
  }, []);

  useEffect(() => {
    const q = activeCat && activeCat !== "All" ? `?category=${encodeURIComponent(activeCat)}` : "";
    api.get(`/products${q}`).then(({ data }) => setProducts(data));
    if (activeCat === "All") params.delete("category");
    else params.set("category", activeCat);
    setParams(params, { replace: true });
    // eslint-disable-next-line
  }, [activeCat]);

  const filtered = useMemo(() => {
    if (!query) return products;
    return products.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()));
  }, [products, query]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="shop-page">
      <Seo
        title={activeCat && activeCat !== "All" ? `${activeCat} Seeds` : "Shop All Seeds"}
        description="Browse 30+ naturally sourced, non-GMO seed varieties: vegetables, herbs, greens, flowers, beans and melons. Every packet $3.50, hand-packed in Amador County, CA."
        path="/shop"
      />
      <div className="mb-12">
        <div className="text-xs tracking-wider-2 uppercase text-sage mb-3">The catalog</div>
        <h1 className="font-display text-5xl sm:text-6xl text-darkRoast">All Seeds</h1>
        <p className="mt-4 text-earth max-w-xl">
          Every packet $3.50. Naturally sourced from an organic supplier, hand-packed in Amador County.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between mb-10 pb-6 border-b border-darkRoast/10">
        <div className="flex flex-wrap gap-2" data-testid="category-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              className={`px-4 py-2 rounded-full text-xs tracking-wider-2 uppercase border transition-colors ${
                activeCat === c
                  ? "bg-darkRoast text-cream border-darkRoast"
                  : "bg-transparent text-darkRoast border-darkRoast/20 hover:border-sage hover:text-sage"
              }`}
              data-testid={`filter-${c.toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="relative w-full lg:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-earth" strokeWidth={1.5} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search varieties…"
            className="w-full bg-transparent border-b border-darkRoast/20 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-sage placeholder:text-earth/50"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-12">
        {filtered.map((p) => {
          const outOfStock = p.stock !== undefined && p.stock <= 0;
          const inCart = items.find((i) => i.product_id === p.product_id)?.quantity || 0;
          return (
            <div key={p.product_id} className="group flex flex-col h-full" data-testid={`product-card-${p.product_id}`}>
              <Link to={`/product/${p.product_id}`} className="block">
                <div className="relative aspect-square overflow-hidden bg-surface border border-darkRoast/10 rounded-sm">
                  <img
                    src={p.image_url}
                    alt={p.name}
                    className={`w-full h-full object-cover transition-transform duration-700 ${outOfStock ? "opacity-50" : "group-hover:scale-105"}`}
                  />
                  {outOfStock && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="bg-darkRoast/80 text-cream text-[10px] tracking-wider-2 uppercase px-3 py-1.5 rounded-full">
                        Out of Stock
                      </span>
                    </div>
                  )}
                </div>
              </Link>
              <div className="mt-5 flex flex-col flex-1">
                <div className="text-[10px] tracking-wider-2 uppercase text-sage mb-1">{p.category}</div>
                <Link to={`/product/${p.product_id}`}>
                  <h3 className="font-display text-2xl text-darkRoast leading-tight hover:text-sage transition-colors">{p.name}</h3>
                </Link>
                <div className="mt-auto pt-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-earth">{p.packet_size_g}g packet</span>
                    <span className="font-medium text-darkRoast">${p.price.toFixed(2)}</span>
                  </div>
                  {p.rating_count > 0 && (
                    <div className="mt-2" data-testid={`card-rating-${p.product_id}`}>
                      <StarRating value={p.rating_avg || 0} count={p.rating_count} size="w-3.5 h-3.5" />
                    </div>
                  )}
                  {outOfStock ? (
                    <button
                      onClick={() => { setNotifyFor(p); setNotifyEmail(""); }}
                      className="mt-4 w-full flex items-center justify-center gap-2 border border-darkRoast/20 text-darkRoast rounded-full py-2.5 text-xs tracking-wider-2 uppercase hover:bg-darkRoast hover:text-cream transition-colors"
                      data-testid={`out-of-stock-${p.product_id}`}
                    >
                      <BellRing className="w-3.5 h-3.5" strokeWidth={1.5} /> Notify me when available
                    </button>
                  ) : inCart > 0 ? (
                    <div className="mt-4 w-full flex items-center justify-between border border-darkRoast/20 rounded-full py-1.5 px-2" data-testid={`cart-stepper-${p.product_id}`}>
                      <button
                        onClick={() => updateQty(p.product_id, inCart - 1)}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-darkRoast hover:text-cream transition-colors"
                        data-testid={`card-dec-${p.product_id}`}
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                      <span className="text-xs tracking-wider-2 uppercase text-darkRoast" data-testid={`card-qty-${p.product_id}`}>{inCart} in cart</span>
                      <button
                        onClick={() => addItem(p, 1)}
                        disabled={p.stock !== undefined && inCart >= p.stock}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-darkRoast hover:text-cream transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-darkRoast"
                        data-testid={`card-inc-${p.product_id}`}
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { const r = addItem(p, 1); if (r.added && !r.capped) toast.success(`Added ${p.name}`); }}
                      className="mt-4 w-full flex items-center justify-center gap-2 border border-darkRoast/20 text-darkRoast rounded-full py-2.5 text-xs tracking-wider-2 uppercase hover:bg-darkRoast hover:text-cream transition-colors"
                      data-testid={`add-to-cart-${p.product_id}`}
                    >
                      <ShoppingBag className="w-3.5 h-3.5" strokeWidth={1.5} /> Add to cart
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-20 text-earth" data-testid="empty-shop">No seeds found.</div>
      )}

      <Dialog open={!!notifyFor} onOpenChange={(o) => !o && setNotifyFor(null)}>
        <DialogContent className="max-w-md bg-cream border-darkRoast/10" data-testid="notify-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-darkRoast">Back in stock alert</DialogTitle>
          </DialogHeader>
          {notifyFor && (
            <form onSubmit={submitNotify} className="space-y-4" data-testid="notify-form">
              <p className="text-sm text-earth">
                We'll email you the moment <span className="text-darkRoast">{notifyFor.name}</span> is back in stock.
              </p>
              <input
                type="email"
                required
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-transparent border-b border-darkRoast/20 py-2.5 text-sm focus:outline-none focus:border-sage placeholder:text-earth/50"
                data-testid="notify-email-input"
              />
              <button
                type="submit"
                disabled={notifying}
                className="w-full bg-darkRoast text-cream py-3 rounded-full text-xs tracking-wider-2 uppercase hover:bg-sage transition-colors disabled:opacity-60"
                data-testid="notify-submit"
              >
                {notifying ? "Adding…" : "Notify me"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
